import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { X, Minus, Plus, Trash2, Copy, Check, Loader2, Bitcoin, Lock } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useAuth } from "@/lib/auth";
import api, { formatApiError } from "@/lib/api";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { TosModal } from "@/components/landing/TosModal";
import { PrivacyPolicyModal } from "@/components/landing/PrivacyPolicyModal";

const inputCls =
  "w-full bg-[#FFFFFF] border border-[#E4E4E7] px-4 py-3 text-sm text-[#09090B] placeholder:text-[#A1A1AA] focus:outline-none focus:border-[#6D28D9] transition-colors";

const labelCls = "block font-mono-clinical text-[10px] tracking-[0.2em] uppercase text-[#71717A] mb-1.5";

const Req = () => <span className="text-[#6D28D9]"> *</span>;

const EMPTY_DETAILS = {
  first_name: "", last_name: "", company: "", country: "United States",
  address1: "", address2: "", city: "", state: "", zip_code: "",
  email: "", industry: "", note: "", tos_accepted: false, research_confirmed: false,
};

const COUNTRIES = [
  "United States", "Canada", "United Kingdom", "Germany", "France", "Netherlands",
  "Spain", "Italy", "Poland", "Ukraine", "Australia", "Ireland", "Switzerland", "Other",
];

const US_STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut","Delaware","District of Columbia",
  "Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa","Kansas","Kentucky","Louisiana","Maine","Maryland",
  "Massachusetts","Michigan","Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire",
  "New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio","Oklahoma","Oregon","Pennsylvania",
  "Rhode Island","South Carolina","South Dakota","Tennessee","Texas","Utah","Vermont","Virginia","Washington",
  "West Virginia","Wisconsin","Wyoming",
];

const INDUSTRIES = [
  "Chemical Engineer", "Pharmacologist", "Pharmaceutical Scientist", "Biotechnologist",
  "Research Scientist", "Laboratory Technician", "Quality Control Analyst", "Lab Executive",
  "Academic Procurement Dep.", "Physician / Clinical Researcher", "Veterinary Researcher", "Other",
];

const StepDot = ({ active, label }) => (
  <span className={`font-mono-clinical text-[10px] tracking-[0.2em] uppercase ${active ? "text-[#6D28D9]" : "text-[#A1A1AA]"}`}>
    {label}
  </span>
);

export const CartDrawer = () => {
  const { items, setQty, remove, clear, isOpen, setIsOpen, total } = useCart();
  const { user, openAuth } = useAuth();
  const [step, setStep] = useState("cart");
  const [details, setDetails] = useState(EMPTY_DETAILS);
  const [order, setOrder] = useState(null);
  const [rate, setRate] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [savedAddrs, setSavedAddrs] = useState([]);
  const [saveAddr, setSaveAddr] = useState(false);
  const [selectedAddr, setSelectedAddr] = useState("");
  const [tosOpen, setTosOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  const [eligible, setEligible] = useState(false);
  const [paypalCfg, setPaypalCfg] = useState(null);

  useEffect(() => {
    if (step === "pay") {
      api.get("/paypal/config").then((r) => setPaypalCfg(r.data)).catch(() => setPaypalCfg({ enabled: false }));
    }
  }, [step]);

  useEffect(() => {
    if (user && user.role !== "admin") {
      const [first, ...rest] = (user.name || "").split(" ");
      setDetails((d) => ({
        ...d,
        first_name: d.first_name || first || "",
        last_name: d.last_name || rest.join(" ") || "",
        email: d.email || user.email || "",
      }));
    }
  }, [user]);

  const goCheckout = () => {
    if (user) {
      api.get("/account/addresses").then((r) => setSavedAddrs(r.data.addresses || [])).catch(() => {});
      api.get("/account/first-order").then((r) => setEligible(!!r.data.eligible)).catch(() => {});
    }
    setStep("details");
  };

  const applyAddress = (a) => {
    setDetails((d) => ({
      ...d,
      first_name: a.first_name || d.first_name,
      last_name: a.last_name || d.last_name,
      company: a.company || "",
      country: a.country || "United States",
      address1: a.address1 || a.shipping_address || "",
      address2: a.address2 || "",
      city: a.city || "",
      state: a.state || "",
      zip_code: a.zip_code || "",
    }));
    setSelectedAddr(a.id);
    setSaveAddr(false);
  };

  const fetchRate = () => {
    fetch("https://api.coingecko.com/api/v3/simple/price?ids=bitcoin&vs_currencies=usd")
      .then((r) => r.json())
      .then((d) => setRate(d?.bitcoin?.usd ?? null))
      .catch(() => setRate(null));
  };

  const close = () => {
    setIsOpen(false);
    if (step === "pay") {
      clear();
      setStep("cart");
      setOrder(null);
      setDetails(EMPTY_DETAILS);
      setSelectedAddr("");
      setSaveAddr(false);
    }
  };

  const set = (k) => (e) => setDetails((d) => ({ ...d, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const placeOrder = async (e) => {
    e.preventDefault();
    if (!details.tos_accepted || !details.research_confirmed) {
      toast.error("Please accept the Terms & Conditions and confirm research use");
      return;
    }
    setPlacing(true);
    try {
      const res = await api.post("/orders", {
        ...details,
        items: items.map((i) => ({ product_id: i.id, name: i.name, code: i.code, size: i.size, price: i.price, qty: i.qty })),
      });
      setOrder(res.data);
      if (saveAddr && user) {
        const { first_name, last_name, company, country, address1, address2, city, state, zip_code } = details;
        api.post("/account/addresses", { first_name, last_name, company, country, address1, address2, city, state, zip_code }).catch(() => {});
      }
      setStep("pay");
      fetchRate();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPlacing(false);
    }
  };

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(order.btc_address);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed — select the address manually.");
    }
  };

  const btcAmount = order
    ? order.btc_expected > 0
      ? order.btc_expected.toFixed(8)
      : rate
        ? (order.total_usd / rate).toFixed(6)
        : null
    : null;
  const shipping = total >= 300 ? 0 : 25;
  const grand = total + shipping;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={close}
            className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            data-testid="cart-drawer"
            className="fixed top-0 right-0 bottom-0 z-[70] w-full max-w-md bg-[#FFFFFF] border-l border-[#E4E4E7] flex flex-col"
          >
            <div className="flex items-center justify-between px-6 h-[72px] border-b border-[#E4E4E7]">
              <div className="flex items-center gap-4">
                <StepDot active={step === "cart"} label="01 Cart" />
                <StepDot active={step === "details"} label="02 Details" />
                <StepDot active={step === "pay"} label="03 Pay BTC" />
              </div>
              <button onClick={close} data-testid="cart-close-button" className="p-2 text-[#71717A] hover:text-[#09090B] transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6">
              {step === "cart" && (
                items.length === 0 ? (
                  <p data-testid="cart-empty" className="text-[#A1A1AA] text-sm font-mono-clinical mt-10 text-center">Your cart is empty.</p>
                ) : (
                  <div className="space-y-px bg-[#E4E4E7] border border-[#E4E4E7]">
                    {items.map((i) => (
                      <div key={i.id} data-testid={`cart-item-${i.code}`} className="bg-[#FFFFFF] p-4 flex items-center justify-between gap-4">
                        <div className="min-w-0">
                          <p className="font-display font-bold uppercase text-sm">{i.name}</p>
                          <p className="font-mono-clinical text-[11px] text-[#A1A1AA]">{i.code} — {i.size}</p>
                          <p className="font-mono-clinical text-xs text-[#6D28D9] mt-1">${(i.price * i.qty).toFixed(0)}</p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <button onClick={() => setQty(i.id, i.qty - 1)} data-testid={`cart-minus-${i.code}`} className="p-1.5 border border-[#E4E4E7] text-[#71717A] hover:text-[#09090B] transition-colors"><Minus className="w-3.5 h-3.5" /></button>
                          <span className="font-mono-clinical text-sm w-6 text-center">{i.qty}</span>
                          <button onClick={() => setQty(i.id, i.qty + 1)} data-testid={`cart-plus-${i.code}`} className="p-1.5 border border-[#E4E4E7] text-[#71717A] hover:text-[#09090B] transition-colors"><Plus className="w-3.5 h-3.5" /></button>
                          <button onClick={() => remove(i.id)} data-testid={`cart-remove-${i.code}`} className="p-1.5 text-[#A1A1AA] hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}

              {step === "details" && (
                <form id="checkout-form" onSubmit={placeOrder} data-testid="checkout-form" className="space-y-4">
                  <p className="font-display text-xl font-bold uppercase tracking-tight text-[#09090B]">Billing details</p>

                  {savedAddrs.length > 0 && (
                    <div data-testid="saved-addresses" className="space-y-2">
                      <p className={labelCls}>Saved addresses</p>
                      {savedAddrs.map((a) => (
                        <button
                          type="button"
                          key={a.id}
                          onClick={() => applyAddress(a)}
                          data-testid={`saved-address-${a.id}`}
                          className={`w-full text-left px-4 py-3 border text-xs transition-colors duration-300 ${
                            selectedAddr === a.id
                              ? "border-[#6D28D9] bg-[#F3F0FF]"
                              : "border-[#E4E4E7] hover:border-[#6D28D9]/50"
                          }`}
                        >
                          <span className="font-bold block text-[#09090B]">{a.name}</span>
                          <span className="text-[#71717A] line-clamp-1">{a.shipping_address}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls} htmlFor="checkout-first-name">First name<Req /></label>
                      <input id="checkout-first-name" required data-testid="checkout-first-name" value={details.first_name} onChange={set("first_name")} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="checkout-last-name">Last name<Req /></label>
                      <input id="checkout-last-name" required data-testid="checkout-last-name" value={details.last_name} onChange={set("last_name")} className={inputCls} />
                    </div>
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="checkout-company">Company name (optional)</label>
                    <input id="checkout-company" data-testid="checkout-company" value={details.company} onChange={set("company")} className={inputCls} />
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="checkout-country">Country / Region<Req /></label>
                    <select id="checkout-country" required data-testid="checkout-country" value={details.country} onChange={set("country")} className={inputCls}>
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="checkout-address1">Street address<Req /></label>
                    <input id="checkout-address1" required placeholder="House number and street name" data-testid="checkout-address1" value={details.address1} onChange={set("address1")} className={inputCls} />
                    <input placeholder="Apartment, suite, unit, etc. (optional)" data-testid="checkout-address2" value={details.address2} onChange={set("address2")} className={`${inputCls} mt-2`} />
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls} htmlFor="checkout-city">Town / City<Req /></label>
                      <input id="checkout-city" required data-testid="checkout-city" value={details.city} onChange={set("city")} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls} htmlFor="checkout-state">State / County<Req /></label>
                      {details.country === "United States" ? (
                        <select id="checkout-state" required data-testid="checkout-state" value={details.state} onChange={set("state")} className={inputCls}>
                          <option value="" disabled>Select…</option>
                          {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      ) : (
                        <input id="checkout-state" required data-testid="checkout-state" value={details.state} onChange={set("state")} className={inputCls} />
                      )}
                    </div>
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="checkout-zip">Postcode / ZIP<Req /></label>
                    <input id="checkout-zip" required data-testid="checkout-zip" value={details.zip_code} onChange={set("zip_code")} className={inputCls} />
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="checkout-email">Email address<Req /></label>
                    <input id="checkout-email" required type="email" data-testid="checkout-email" value={details.email} onChange={set("email")} className={inputCls} />
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="checkout-industry">Industry / Profession<Req /></label>
                    <select id="checkout-industry" required data-testid="checkout-industry" value={details.industry} onChange={set("industry")} className={inputCls}>
                      <option value="" disabled>Please select…</option>
                      {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
                    </select>
                  </div>

                  <div>
                    <label className={labelCls} htmlFor="checkout-note">Order note (optional)</label>
                    <input id="checkout-note" data-testid="checkout-note" value={details.note} onChange={set("note")} className={inputCls} />
                  </div>

                  {user && (
                    <label className="flex items-center gap-2.5 cursor-pointer group" data-testid="save-address-label">
                      <input
                        type="checkbox"
                        checked={saveAddr}
                        onChange={(e) => setSaveAddr(e.target.checked)}
                        data-testid="save-address-checkbox"
                        className="w-4 h-4 shrink-0 accent-[#6D28D9] cursor-pointer"
                      />
                      <span className="text-xs text-[#71717A] group-hover:text-[#52525B] transition-colors">Save this address to my account</span>
                    </label>
                  )}

                  {!user && (
                    <p className="text-[11px] text-[#A1A1AA] leading-relaxed" data-testid="checkout-discount-hint">
                      <button type="button" onClick={() => { setIsOpen(false); openAuth("register"); }} data-testid="checkout-signin-hint" className="text-[#6D28D9] underline underline-offset-2 hover:text-[#5B21B6]">
                        Create an account
                      </button>{" "}
                      and get <strong className="text-[#6D28D9]">$15 off</strong> your first order — plus saved addresses for next time.
                    </p>
                  )}

                  <label className="flex items-start gap-2.5 cursor-pointer group" data-testid="tos-checkbox-label">
                    <input
                      type="checkbox"
                      required
                      checked={details.tos_accepted}
                      onChange={set("tos_accepted")}
                      data-testid="checkout-tos-checkbox"
                      className="mt-0.5 w-4 h-4 shrink-0 accent-[#6D28D9] cursor-pointer"
                    />
                    <span className="text-xs text-[#71717A] leading-relaxed">
                      I accept the{" "}
                      <button type="button" onClick={() => setTosOpen(true)} data-testid="checkout-tos-link" className="text-[#6D28D9] underline underline-offset-2 hover:text-[#5B21B6]">
                        Terms &amp; Conditions
                      </button>
                      <Req />
                    </span>
                  </label>

                  <label className="flex items-start gap-2.5 cursor-pointer group" data-testid="research-checkbox-label">
                    <input
                      type="checkbox"
                      required
                      checked={details.research_confirmed}
                      onChange={set("research_confirmed")}
                      data-testid="checkout-research-checkbox"
                      className="mt-0.5 w-4 h-4 shrink-0 accent-[#6D28D9] cursor-pointer"
                    />
                    <span className="text-xs text-[#71717A] leading-relaxed">
                      Please confirm: The products are intended for laboratory research use only.<Req />
                    </span>
                  </label>

                  <div data-testid="order-summary" className="border border-[#E4E4E7] p-4 space-y-2 bg-[#FBFBFC]">
                    <p className="font-display font-bold uppercase text-sm tracking-tight text-[#09090B] pb-2 border-b border-[#E4E4E7]">Your order</p>
                    {items.map((i) => (
                      <div key={i.id} className="flex justify-between text-xs text-[#52525B]">
                        <span>{i.name} {i.size} × {i.qty}</span>
                        <span className="font-mono-clinical">${(i.price * i.qty).toFixed(2)}</span>
                      </div>
                    ))}
                    <div className="flex justify-between text-xs text-[#52525B] pt-2 border-t border-[#E4E4E7]">
                      <span>Subtotal</span>
                      <span className="font-mono-clinical">${total.toFixed(2)}</span>
                    </div>
                    {user && eligible && (
                      <div className="flex justify-between text-xs text-[#6D28D9]" data-testid="discount-row">
                        <span>New account discount</span>
                        <span className="font-mono-clinical">−${Math.min(15, total).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xs text-[#52525B]">
                      <span>Shipping</span>
                      <span className="font-mono-clinical">{shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}</span>
                    </div>
                    <div className="flex justify-between text-sm font-bold text-[#09090B] pt-2 border-t border-[#E4E4E7]">
                      <span>Total</span>
                      <span className="font-mono-clinical" data-testid="order-summary-total">${(grand - (user && eligible ? Math.min(15, total) : 0)).toFixed(2)}</span>
                    </div>
                  </div>

                  <p className="text-[11px] text-[#A1A1AA] leading-relaxed" data-testid="checkout-privacy-note">
                    Your personal data will be used to process your order, support your experience throughout this
                    website, and for other purposes described in our{" "}
                    <button type="button" onClick={() => setPrivacyOpen(true)} data-testid="checkout-privacy-link" className="text-[#6D28D9] underline underline-offset-2 hover:text-[#5B21B6]">
                      privacy policy
                    </button>
                    .
                  </p>
                </form>
              )}

              {step === "pay" && order && order.status === "paid" && (
                <div data-testid="payment-success" className="border border-[#6D28D9]/30 bg-[#F3F0FF] p-6 text-center space-y-3">
                  <Check className="w-8 h-8 text-[#6D28D9] mx-auto" />
                  <p className="font-display text-xl font-bold uppercase tracking-tight text-[#09090B]">Payment received</p>
                  <p className="text-sm text-[#52525B] leading-relaxed">
                    Order № {order.id.slice(-8).toUpperCase()} is confirmed{order.payment_method === "PayPal" ? " (paid via PayPal)" : ""}.
                    It ships within 48h, cold-chain packaged — a confirmation email is on its way.
                  </p>
                </div>
              )}

              {step === "pay" && order && order.status !== "paid" && (
                <div data-testid="btc-payment-panel" className="space-y-6">
                  {paypalCfg?.enabled && (
                    <div className="space-y-3" data-testid="paypal-section">
                      <p className="font-mono-clinical text-[10px] tracking-[0.25em] uppercase text-[#6D28D9]">Pay with PayPal</p>
                      <PayPalScriptProvider options={{ clientId: paypalCfg.client_id, currency: "USD", intent: "capture" }}>
                        <PayPalButtons
                          style={{ layout: "vertical", shape: "rect", label: "pay" }}
                          forceReRender={[order.id, order.total_usd]}
                          createOrder={async () => {
                            const r = await api.post("/paypal/create-order", { order_id: order.id });
                            return r.data.id;
                          }}
                          onApprove={async (data) => {
                            try {
                              const r = await api.post("/paypal/capture", { paypal_order_id: data.orderID, order_id: order.id });
                              if (r.data.status === "paid") {
                                setOrder((o) => ({ ...o, status: "paid", payment_method: "PayPal" }));
                                toast.success("Payment received — your order is confirmed");
                              }
                            } catch (err) {
                              toast.error(formatApiError(err));
                            }
                          }}
                          onError={() => toast.error("PayPal payment failed — try again or pay with Bitcoin")}
                        />
                      </PayPalScriptProvider>
                      <div className="flex items-center gap-3 py-1">
                        <span className="flex-1 h-px bg-[#E4E4E7]" />
                        <span className="font-mono-clinical text-[10px] tracking-[0.25em] uppercase text-[#A1A1AA]">or pay with Bitcoin</span>
                        <span className="flex-1 h-px bg-[#E4E4E7]" />
                      </div>
                    </div>
                  )}
                  <div className="border border-[#6D28D9]/30 bg-[#6D28D9]/5 p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <Bitcoin className="w-4 h-4 text-[#6D28D9]" />
                      <p className="font-mono-clinical text-[10px] tracking-[0.25em] uppercase text-[#6D28D9]">Pay with Bitcoin</p>
                    </div>
                    <p className="font-display text-3xl font-black text-[#09090B]">
                      ${order.total_usd.toFixed(2)}
                      {btcAmount && <span className="text-sm font-mono-clinical font-normal text-[#71717A] ml-3">≈ {btcAmount} BTC</span>}
                    </p>
                    {!btcAmount && <p className="text-xs text-[#A1A1AA] mt-1">Send the BTC equivalent of the USD total at current market rate.</p>}
                    <p className="font-mono-clinical text-[11px] text-[#A1A1AA] mt-2">
                      Order № {order.id.slice(-8).toUpperCase()} — {order.discount_usd > 0 ? `−$${order.discount_usd.toFixed(2)} new account discount applied` : ""}{order.discount_usd > 0 ? ", " : ""}{order.shipping_usd === 0 ? "free shipping applied" : `includes $${order.shipping_usd.toFixed(2)} shipping`}
                    </p>
                  </div>

                  <div className="flex justify-center">
                    <img
                      src="/btc-qr.jpg"
                      alt="Scan to pay with Bitcoin"
                      data-testid="btc-qr-code"
                      className="w-52 h-52 object-cover border border-[#E4E4E7]"
                    />
                  </div>

                  <div>
                    <p className="font-mono-clinical text-[10px] tracking-[0.25em] uppercase text-[#A1A1AA] mb-2">Send BTC to</p>
                    <div className="flex items-stretch border border-[#E4E4E7]">
                      <code data-testid="btc-address" className="flex-1 font-mono-clinical text-xs text-[#6D28D9] px-4 py-3.5 break-all bg-[#FFFFFF]">
                        {order.btc_address}
                      </code>
                      <button onClick={copyAddress} data-testid="btc-copy-button" className="px-4 border-l border-[#E4E4E7] text-[#71717A] hover:text-[#6D28D9] transition-colors">
                        {copied ? <Check className="w-4 h-4 text-[#6D28D9]" /> : <Copy className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <p className="text-xs text-[#A1A1AA] leading-relaxed">
                    After sending, keep your transaction ID and order number. Payment is verified on-chain and your
                    order ships within 48h of one network confirmation, cold-chain packaged.
                  </p>
                </div>
              )}
            </div>

            <div className="border-t border-[#E4E4E7] px-6 py-5">
              {step === "cart" && items.length > 0 && (
                <>
                  <div className="space-y-1.5 mb-4 font-mono-clinical text-xs">
                    <div className="flex justify-between text-[#71717A] uppercase tracking-[0.15em]">
                      <span>Subtotal</span>
                      <span data-testid="cart-subtotal" className="text-[#09090B]">${total.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-[#71717A] uppercase tracking-[0.15em]">
                      <span>Shipping</span>
                      <span data-testid="cart-shipping" className={shipping === 0 ? "text-[#6D28D9]" : "text-[#09090B]"}>
                        {shipping === 0 ? "FREE" : `$${shipping.toFixed(2)}`}
                      </span>
                    </div>
                    <div className="flex justify-between text-[#09090B] uppercase tracking-[0.15em] font-bold border-t border-[#E4E4E7] pt-2">
                      <span>Total</span>
                      <span data-testid="cart-total">${grand.toFixed(2)}</span>
                    </div>
                    <div className="pt-2" data-testid="free-shipping-bar">
                      <div className="flex justify-between text-[10px] text-[#A1A1AA] normal-case tracking-normal mb-1.5">
                        <span data-testid="free-shipping-hint">
                          {shipping === 0 ? "Free shipping unlocked" : `You're $${(300 - total).toFixed(2)} from FREE shipping`}
                        </span>
                        <span>{Math.min(100, Math.round((total / 300) * 100))}%</span>
                      </div>
                      <div className="h-1 bg-[#E4E4E7] overflow-hidden">
                        <div
                          className="h-full bg-[#6D28D9] transition-[width] duration-700"
                          style={{ width: `${Math.min(100, (total / 300) * 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <button onClick={goCheckout} data-testid="cart-checkout-button" className="w-full py-4 bg-[#6D28D9] text-white font-mono-clinical text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#5B21B6] transition-colors flex items-center justify-center gap-2">
                    Proceed to Checkout
                  </button>
                </>
              )}
              {step === "details" && (
                <div className="flex gap-3">
                  <button onClick={() => setStep("cart")} data-testid="checkout-back-button" className="px-6 py-4 border border-[#E4E4E7] font-mono-clinical text-xs tracking-[0.15em] uppercase text-[#71717A] hover:text-[#09090B] transition-colors">
                    Back
                  </button>
                  <button type="submit" form="checkout-form" disabled={placing} data-testid="place-order-button" className="flex-1 py-4 bg-[#6D28D9] text-white font-mono-clinical text-xs tracking-[0.15em] uppercase font-bold hover:bg-[#5B21B6] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                    {placing && <Loader2 className="w-4 h-4 animate-spin" />}
                    Place Order — ${grand.toFixed(2)}
                  </button>
                </div>
              )}
              {step === "pay" && (
                <button onClick={close} data-testid="payment-done-button" className="w-full py-4 border border-[#E4E4E7] font-mono-clinical text-xs tracking-[0.15em] uppercase text-[#52525B] hover:border-[#6D28D9] hover:text-[#6D28D9] transition-colors">
                  Done — I&apos;ll Send Payment
                </button>
              )}
            </div>
          </motion.aside>
          <TosModal open={tosOpen} onClose={() => setTosOpen(false)} />
          <PrivacyPolicyModal open={privacyOpen} onClose={() => setPrivacyOpen(false)} />
        </>
      )}
    </AnimatePresence>
  );
};
